<template>
    <div class="search-widget-container flight-search">
        <div class="flex-items" v-if="!iframeWidget">
            <div class="title">
                <span v-if="!searchQuery.originName && !searchQuery.destinationName">{{$t('search')}} </span>{{$t('flights')}}
                <span class="location" v-if="searchQuery.originName">{{$t('fromOrigin', {origin: searchQuery.originName})}} </span>
                <span class="location" v-if="searchQuery.destinationName">{{$t('toDestination', {destination: searchQuery.destinationName})}}</span>
            </div>

            <div class="dates" v-if="searchQuery.oneWayDate">
                <span v-if="tripType === 'Round Trip' && searchQuery.roundTripDates && searchQuery.roundTripDates.start && searchQuery.roundTripDates.end">{{ searchQuery.roundTripDates.start | moment("ddd, MMM Do") }} - {{ searchQuery.roundTripDates.end | moment("ddd, MMM Do") }}</span>
                <span v-else>{{ searchQuery.oneWayDate | moment("ddd, MMM Do") }} </span>
            </div>

            <div class="toggle"
                v-if="showToggle" 
                @click="showSearch = !showSearch" 
                title="Update Search">
                <span v-if="!showSearchWidget">{{$t('editSearch')}} <i class="fal fa-search"></i></span>
                <span v-else>{{$t('hideSearch')}} <i class="fal fa-times"></i></span> 
            </div>
            
        </div>
        <div class="search-widget" v-if="showSearch">
            <h2 v-if="iframeWidget">{{$t('searchFlights')}}</h2>
            <div class="option-selectors">
              <v-select
                  name="Trip Type"
                  class="type"
                  v-model="tripType"
                  :clearable="false"
                  :components="{OpenIndicator}"
                  :searchable="false"
                  label="text"
                  :reduce="text => text.value"
                  :options="[
                      { value: 'Round Trip', text: $t('roundTrip') },
                      { value: 'One Way', text: $t('oneWay') }
                  ]">
              </v-select>

              <div class="passenger-counts" v-click-outside="hidePaxToggles">
                    <a href="#" id="paxCountLink" :class="{open : showPaxCounts}" class="pax-count-link" @click="togglePaxWidget()" title="1 Adult">
                        <span class="passenger-set">
                            <span>
                                {{ searchQuery.passengers[0].count }} 
                                <span v-if="searchQuery.passengers[0].count > 1">{{ $t('adults') }}</span>
                                <span v-else>{{ $t('adult') }}</span>
                            </span>
                            <span v-if="searchQuery.passengers[1].count">, 
                                {{ searchQuery.passengers[1].count }} 
                                <span v-if="searchQuery.passengers[1].count > 1">{{ $t('children') }}</span>
                                <span v-else>{{ $t('child') }}</span>
                            </span>
                            <span v-if="searchQuery.passengers[2].count">, 
                                {{ searchQuery.passengers[2].count }} 
                                <span v-if="searchQuery.passengers[2].count > 1">{{ $t('infants') }}</span>
                                <span v-else>{{ $t('infant') }}</span>    
                            </span>
                        </span>
                        <i class="fa fa-chevron-down"></i>
                    </a>

                    <div class="passenger-toggles" v-if="showPaxCounts">
                        <div class="overlay"></div>
                        <div class="passenger-toggle">
                            <div class="pax-label">
                                {{$t('adults')}} 
                                <span>{{ $t('over', {number: '11'}) }}</span>
                            </div>
                            <div class="toggles">
                                <button class="btn btn-sec btn-sm" @click="() => {searchQuery.passengers[0].count -= (searchQuery.passengers[0].count !== 1 ? 1 : 0); searchQuery.passengers[2].count = searchQuery.passengers[2].count > searchQuery.passengers[0].count ? searchQuery.passengers[0].count : searchQuery.passengers[2].count;}"><i class="fal fa-minus"></i></button>
                                <input type="text" v-model="searchQuery.passengers[0].count" class="count" />
                                <button class="btn btn-sec btn-sm" @click="searchQuery.passengers[0].count += ((searchQuery.passengers[0].count + searchQuery.passengers[1].count + searchQuery.passengers[2].count) !== 9 ? 1 : 0)"><i class="fal fa-plus"></i></button>
                            </div>
                        </div>

                        <div class="passenger-toggle">
                            <div class="pax-label">
                                {{$t('children')}} 
                                <span>2 - 11</span>
                            </div>
                            <div class="toggles">
                                <button class="btn btn-sec btn-sm" @click="searchQuery.passengers[1].count -= (searchQuery.passengers[1].count !== 0 ? 1 : 0)"><i class="fal fa-minus"></i></button>
                                <input type="text" v-model="searchQuery.passengers[1].count" class="count" />
                                <button class="btn btn-sec btn-sm" @click="searchQuery.passengers[1].count += ((searchQuery.passengers[0].count + searchQuery.passengers[1].count + searchQuery.passengers[2].count) < 9 ? 1 : 0)"><i class="fal fa-plus"></i></button>
                            </div>
                        </div>

                        <div class="passenger-toggle">
                            <div class="pax-label">
                                {{$t('infants')}} 
                                <span>{{$t('under', {number: '2'})}}</span>
                            </div>
                            <div class="toggles">
                                <button class="btn btn-sec btn-sm" @click="searchQuery.passengers[2].count -= (searchQuery.passengers[2].count !== 0 ? 1 : 0)"><i class="fal fa-minus"></i></button>
                                <input type="text" v-model="searchQuery.passengers[2].count" class="count" />
                                <button class="btn btn-sec btn-sm" @click="searchQuery.passengers[2].count += ((searchQuery.passengers[0].count + searchQuery.passengers[1].count + searchQuery.passengers[2].count) < 9 && searchQuery.passengers[2].count < searchQuery.passengers[0].count ? 1 : 0)"><i class="fal fa-plus"></i></button>
                            </div>
                        </div>
                    </div>
                </div>

              <v-select
                  name="Seat Class"
                  class="class"
                  v-model="searchQuery.seatClass"
                  :clearable="false"
                  :components="{OpenIndicator}"
                  :searchable="false"
                  label="name"
                  :reduce="item => item.value"
                  :options="$t('seatOptions').filter(item => !item.hasOwnProperty('visible') || item.visible)">
              </v-select>
            </div>

            <div class="form-fields">
                <div class="destinations">
                    <div class="form-group">
                        <v-select
                            v-model="searchQuery.origin"
                            @search="searchOriginLocation"
                            :options="originResults"
                            :placeholder="$t('origin')"
                            label="name"
                            :reduce="item => item"
                            :filterable="false"
                            >
                            <template slot="no-options">
                                {{ $t('enterACityOrAirport') }}...
                            </template>
                            <template slot="option" slot-scope="option">
                                <div class="autocomplete-result">
                                    <i class="fal fa-city" v-if="option.type === 'city'"></i>
                                    <i class="fal fa-plane" v-if="option.type === 'airport'"></i>
                                    {{ option.name + ( option.code ? ` (${option.code})` : '' ) }}
                                </div>
                            </template>
                            <template slot="selected-option" slot-scope="option">
                                <div class="autocomplete-result selected">
                                    <i class="fal fa-city" v-if="option.type === 'city'"></i>
                                    <i class="fal fa-plane" v-if="option.type === 'airport'"></i>
                                    {{ option.name + ( option.code ? ` (${option.code})` : '' ) }}
                                </div>
                            </template>
                        </v-select>
                    </div>

                    <div class="form-group">
                        <v-select
                            v-model="searchQuery.destination"
                            @search="searchDestinationLocation"
                            :options="destinationResults"
                            :placeholder="$t('destination')"
                            label="name"
                            :reduce="item => item"
                            :filterable="false"
                            >
                            <template slot="no-options">
                                {{ $t('enterACityOrAirport') }}...
                            </template>
                            <template slot="option" slot-scope="option">
                                <div class="autocomplete-result">
                                    <i class="fal fa-city" v-if="option.type === 'city'"></i>
                                    <i class="fal fa-plane" v-if="option.type === 'airport'"></i>
                                    {{ option.name + ( option.code ? ` (${option.code})` : '' ) }}
                                </div>
                            </template>
                            <template slot="selected-option" slot-scope="option">
                                <div class="autocomplete-result selected">
                                    <i class="fal fa-city" v-if="option.type === 'city'"></i>
                                    <i class="fal fa-plane" v-if="option.type === 'airport'"></i>
                                    {{ option.name + ( option.code ? ` (${option.code})` : '' ) }}
                                </div>
                            </template>
                        </v-select>
                    </div>
                </div>
                
                <div class="dates">
                    <div class="form-group one-way" v-if="tripType !== 'Round Trip'">
                        <date-picker
                            v-model="searchQuery.oneWayDate"
                            :input-props='{
                                class: "date-picker",
                                placeholder: $t("departureDate"),
                                readonly: true,
                            }'
                            :popover='{
                                visibility: "focus"
                            }'
                            :min-date="new Date()"
                            value="null"
                        >
                        </date-picker>
                        <i class="fal fa-calendar"></i>
                    </div>
                    <div class="form-group round-trip" v-else>
                        <date-picker
                                mode="range"
                                v-model="searchQuery.roundTripDates"
                                :input-props='{
                                class: "date-picker",
                                placeholder: $t("departureAndReturnDates"),
                                readonly: true
                            }'
                                :popover='{
                                visibility: "focus"
                            }'
                                :min-date="new Date()"
                                value="null">
                        </date-picker>
                        <i class="fal fa-calendar"></i>
                    </div>
                </div>

                <button class="btn btn-search" @click="executeSearch()">{{ $t("searchFlights") }}</button>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from 'vue';
import FlightsApi from "@/services/flightsApi";
import DatePicker from 'v-calendar/lib/components/date-picker.umd';
Vue.component('date-picker', DatePicker);
import 'vue-datetime/dist/vue-datetime.css';
import { mapGetters } from "vuex";
import { searchWidgetMixin } from '@/mixins/searchWidgetMixin';

export default {
    data () {
        return {
            api: null,
            flightsApi: null,
            originResults: [],
            destinationResults: [],
            showPaxCounts: false,
            tripType: 'Round Trip',
            showSearch: null,
            moment: this.$moment,
            showToggle: null,
            searchQuery: {
              origin: '',
              originName: '',
              destination: '',
              destinationName: '',
              oneWayDate: null,
              roundTripDates: null,
              passengers: [
                  {
                      label: 'Adults',
                      count: 1
                  },
                  {
                      label: 'Children',
                      count: 0
                  },
                  {
                      label: 'Infants',
                      count: 0
                  }
              ],
              seatClass: 'economy'
            },
            OpenIndicator: {
                render: createElement => createElement('i', {class: {'fa fa-chevron-down': true}}),
            }
        }
    },

    props: {
        showSearchWidget: Boolean,
        showSearchToggle: Boolean,
        iframeWidget: Boolean
    },

    mixins: [searchWidgetMixin],

    mounted() {
        let self = this;
        self.showSearch = self.showSearchWidget;
        self.showToggle = self.showSearchToggle;
        self.flightsApi = new FlightsApi();
        self.setupParams();
    },

    methods: {
        setupParams: async function () {
            let self = this;
            let locationApi = await import('@/services/locationApi.js');

            self.api = new locationApi.default();
            let params = self.$route.params;
            if (params.hasOwnProperty('origin') && params.origin) {
                let origin = self.api.getByIataCode(params.origin);
                this.setOrigin(origin);
            }
            if (params.hasOwnProperty('destination') && params.destination) {
                let destination = self.api.getByIataCode(params.destination);
                self.setDestination(destination);
            }
            if (params.hasOwnProperty('departureDate') && params.departureDate) {
                let departureDate = self.$moment(params.departureDate, 'YYYY-MM-DD').toDate();
                self.searchQuery.oneWayDate = departureDate;
                self.tripType = 'One Way';
            }
            if (params.hasOwnProperty('returnDate') && params.returnDate) {
                let returnDate = self.$moment(params.returnDate, 'YYYY-MM-DD').toDate();
                self.searchQuery.roundTripDates = {start: self.searchQuery.oneWayDate, end: returnDate}
                self.tripType = 'Round Trip';
            }

            let queryParams = self.$route.query;

            if (queryParams.hasOwnProperty('adults')) {
                let count = parseInt(queryParams.adults);
                if (!isNaN(count)) {
                self.searchQuery.passengers[0].count = count;
                }
            }
            if (queryParams.hasOwnProperty('children')) {
                let count = parseInt(queryParams.children);
                if (!isNaN(count)) {
                self.searchQuery.passengers[1].count = count;
                }
            }
            if (queryParams.hasOwnProperty('infants')) {
                let count = parseInt(queryParams.infants);
                if (!isNaN(count)) {
                self.searchQuery.passengers[2].count = count;
                }
            }
            if (queryParams.hasOwnProperty('seatClass')) {
                self.searchQuery.seatClass = queryParams.seatClass;
            }
        },
        togglePaxWidget: function() {
            this.showPaxCounts = !this.showPaxCounts;
        },
        hidePaxToggles: function() {
            this.showPaxCounts = false
        },
        searchDestinationLocationUsingApi: function(term) {
          let self = this;
          return new Promise((resolve, reject) => {
            self.flightsApi.suggest(term)
                .then(results => { resolve(results.data); })
                .catch(ex => { reject(ex); });
          });
        },
        
        searchOriginLocation: function(search, loading) {
            let self = this;
            if (self.timeout) clearTimeout(self.timeout);
            self.timeout = setTimeout(() => {
                if(search != '') {
                    loading(true);
                    self.searchDestinationLocationUsingApi(search)
                    .then(function (results) {
                        self.originResults = results;
                        loading(false);
                    })
                    .catch(() => { });
                }
            }, 500);
        },

        searchDestinationLocation: function(search, loading) {
            let self = this;
            if (self.timeout) clearTimeout(self.timeout);
            self.timeout = setTimeout(() => {
                if(search != '') {
                    loading(true);
                    self.searchDestinationLocationUsingApi(search)
                    .then(function (results) {
                        self.destinationResults = results;
                        loading(false);
                    })
                    .catch(() => { });
                }
            }, 500);
        },
        setOrigin (origin) {
            this.searchQuery.origin = origin.code;
            this.searchQuery.originName = origin.name;
            this.originResults = [
                origin
            ]
        },
        setDestination (destination) {
            this.searchQuery.destination = destination.code;
            this.searchQuery.destinationName = destination.name;
            this.destinationResults = [
                destination
            ]
        },
        clearOrigin() {
            this.searchQuery.origin = '';
            this.searchQuery.originName = '';
        },
        clearDestination() {
            this.searchQuery.destination = '';
            this.searchQuery.destinationName = '';
        },
        clearPassengers() {
            this.searchQuery.passengers[0].count = 1;
            this.searchQuery.passengers[1].count = 0;
            this.searchQuery.passengers[2].count = 0;
            this.showPaxCounts = false
        },
        executeSearch() {
          let self = this;
            let locale = this.$route.params.locale;
            let route = `/${locale}/flights/search`;

            this.$validator.validateAll().then((result) => {
                route += '/' + this.getCurrencyCode;
                route += '/' + this.searchQuery.origin.code + '/' + this.searchQuery.destination.code;
                if (this.tripType === 'Round Trip') {
                    route += '/' + self.$moment(this.searchQuery.roundTripDates.start).format("YYYY-MM-DD");
                    route += '/' + self.$moment(this.searchQuery.roundTripDates.end).format("YYYY-MM-DD");
                }
                else {
                  route += '/' + self.$moment(this.searchQuery.oneWayDate).format("YYYY-MM-DD");
                }
                
                route += '?adults=' + this.searchQuery.passengers[0].count;
                route += '&children=' + this.searchQuery.passengers[1].count;
                route += '&infants=' + this.searchQuery.passengers[2].count;
                route += '&seatClass=' + this.searchQuery.seatClass;
                route += '&sortOrder=' + this.getAgencyDisplay.products.flights.sortOrder;

                if (result) {
                    this.searchQuery.currency = this.getCurrencyCode;
                    this.$store.dispatch('saveSearchCriteria', this.searchQuery);


                    if(this.iframeWidget) {
                        top.window.location.href = route;
                    } else {
                        this.$router.push(route);
                    }
                }
            });
        }
    },
    computed: {
      ...mapGetters([
        'getCurrencyCode',
        'getAgencyDisplay'
      ])
    }
}
</script>